import {
  getAcceptedGroups,
  getAllConnectedUsersExtendedWithConnection,
  getGroupInvites,
  getIsLoadingConnections,
  isAcceptedConnection,
  isConnectionInvite,
  isConnectionRequest,
  useAppSelector,
} from "@lib/store";

import { Loader } from "@atoms";
import { Canvas, Fullbody, Heading } from "@templates";
import { useEffect, useMemo } from "react";
import { trackPageComponentedRendered } from "@lib/infrastructure";
import { H1 } from "@quarks";
import { ConnectionSuggestionSection, ConnectionsSection, GroupsSection } from "./components";
import { NewRequestsSection } from "./components/NewRequestsSection/NewRequestsSection";
import { useTranslation } from "react-i18next";

export const ConnectionsRevamp = () => {
  const { t } = useTranslation();
  const isLoadingConnections = useAppSelector(getIsLoadingConnections);
  const allConnectedUsersExtendedWithConnection = useAppSelector(getAllConnectedUsersExtendedWithConnection);
  const groupInvites = useAppSelector(getGroupInvites);
  const groups = useAppSelector(getAcceptedGroups);

  const isLoadingGroups = useAppSelector(state => state.data.groups.groups?.status === "pending");

  const connections = useMemo(() => {
    const pendingIncoming = allConnectedUsersExtendedWithConnection.filter(isConnectionInvite);
    const pendingOutgoing = allConnectedUsersExtendedWithConnection.filter(isConnectionRequest);
    const accepted = allConnectedUsersExtendedWithConnection.filter(isAcceptedConnection);

    return {
      pendingIncoming,
      pendingOutgoing,
      accepted,
    };
  }, [allConnectedUsersExtendedWithConnection]);

  const hasConnections = allConnectedUsersExtendedWithConnection.length > 0;
  const hasGroups = groups.length > 0;

  const isLoadingInitialData = (isLoadingGroups && !hasGroups) || (isLoadingConnections && !hasConnections);

  useEffect(() => {
    trackPageComponentedRendered("connections", !isLoadingInitialData);
  }, [isLoadingInitialData]);

  if (isLoadingInitialData) {
    return (
      <Canvas centerContent>
        <Loader />
      </Canvas>
    );
  }

  return (
    <>
      <Heading className="sm:hidden px-4">
        <H1 className="col-span-full">{t("connection:ConnectedCoworkers")}</H1>
      </Heading>
      <Canvas />
      <Fullbody className="sm:p-4 md:p-8 md:py-14">
        <div className="md:flex justify-center flex-1">
          <div className="grid gap-4 sm:gap-[var(--gutterWidth)] grid-cols-1 md:grid-cols-[minmax(0,var(--mainWidth))_minmax(0,var(--sideWidth))]">
            <H1 className="hidden sm:block col-span-full">{t("connection:ConnectedCoworkers")}</H1>

            <div className="grid w-full gap-4 sm:gap-[var(--gutterWidth)] md:col-start-1 order-1">
              <GroupsSection
                groups={groups}
                className="col-start-1 col-end-1 rounded-none sm:rounded-lg+"
              />
              <ConnectionsSection
                acceptedConnections={connections.accepted}
                className="col-start-1 col-end-1 rounded-none sm:rounded-lg+"
              />
            </div>
            <aside className="grid w-full gap-4 sm:gap-[var(--gutterWidth)] md:col-start-2 order-0 md:order-2 self-start">
              <NewRequestsSection
                className="rounded-none sm:rounded-lg+"
                pendingConnections={connections.pendingIncoming}
                pendingGroupInvites={groupInvites}
              />

              <ConnectionSuggestionSection className="rounded-none sm:rounded-lg+" />
            </aside>
          </div>
        </div>
      </Fullbody>
    </>
  );
};
